import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  AdminLayout,
  DashboardPage,
  UsersPage,
} from '../admin';
import AdminLogin from './pages/AdminLogin';
import ResetPassword from './pages/ResetPassword';
import ConfigManagementPage from './pages/config/ConfigManagementPage';
import MediaManagementPage from './pages/media/MediaManagementPage';
import RichEditorTestPage from './pages/test/RichEditorTestPage';
import SchemeManagerPage from './pages/manager/SchemeManagerPage';
import RolesPage from './pages/roles/RolesPage';
import BackupManagementPage from './pages/backup/BackupManagementPage';
import ProtectedRoute from '../ProtectedRoute';
import AuthService from '../../services/AuthService';
import { useAuthErrorHandler } from '../../hooks/useAuthErrorHandler';

/**
 * Admin Router
 * Manages all admin panel routes with layout wrapper
 */
export const AdminRouter: React.FC = () => {
  // Listen for authentication errors from API calls
  useAuthErrorHandler();

  // Check if user is authenticated
  const isAuthenticated = AuthService.isAuthenticated();

  // If not authenticated, show only login page
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<AdminLogin />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/admin/login" replace />} />
      </Routes>
    );
  }

  // If authenticated, show admin dashboard with layout wrapped in ProtectedRoute
  return (
    <Routes>
      {/* Accessible even when authenticated (first-login password reset) */}
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* All other routes require layout */}
      <Route path="/*" element={
        <ProtectedRoute>
          <AdminLayout>
            <Routes>
              {/* Dashboard */}
              <Route path="/" element={<DashboardPage />} />
              <Route path="/dashboard" element={<Navigate to="/admin" replace />} />

              {/* User Routes */}
              <Route path="/users" element={<UsersPage />} />

              {/* Role Management Routes (admin only) */}
              <Route path="/roles" element={<ProtectedRoute requiredRole="admin"><RolesPage /></ProtectedRoute>} />

              {/* Media Management Routes */}
              <Route path="/media" element={<MediaManagementPage />} />

              {/* Configuration Management Routes */}
              <Route path="/configs" element={<ProtectedRoute requiredRole="admin"><ConfigManagementPage /></ProtectedRoute>} />

              {/* Scheme Manager Routes - pinned schemas dedicated management pages */}
              <Route path="/manager/:schemeKey" element={<SchemeManagerPage />} />

              {/* Backup Management Routes (admin only) */}
              <Route path="/backup" element={<ProtectedRoute requiredRole="admin"><BackupManagementPage /></ProtectedRoute>} />

              {/* Test Routes */}
              <Route path="/test" element={<RichEditorTestPage />} />

              {/* Not Found */}
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </AdminLayout>
        </ProtectedRoute>
      } />
    </Routes>
  );
};

export default AdminRouter;
